import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, FileText, Calendar, ArrowRight } from 'lucide-react';
import { format } from 'date-fns';

export default function CoachingHistoryPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sessions, setSessions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchSessions() {
      if (!user?.id) return;

      try {
        const { data: authData } = await supabase.auth.getUser();
        if (!authData.user) return;

        const { data, error } = await supabase
          .from('coaching_sessions')
          .select(`
            *,
            coaching_templates (
              name,
              category,
              output_document
            )
          `)
          .eq('auth_user_id', authData.user.id)
          .order('created_at', { ascending: false });

        if (error) throw error;
        setSessions(data || []);
      } catch (error) {
        console.error('Error fetching sessions:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchSessions();
  }, [user?.id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Coaching History</h1>
        <p className="text-muted-foreground">
          View your past coaching sessions and generated documents
        </p>
      </div>

      {sessions.length === 0 ? (
        <Card className="p-12 text-center">
          <FileText className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
          <h3 className="text-xl font-semibold mb-2">No Sessions Yet</h3>
          <p className="text-muted-foreground mb-6">
            Start a coaching session to see your history here
          </p>
          <Button onClick={() => navigate('/simulations')}>
            Browse Coaching Sessions
          </Button>
        </Card>
      ) : (
        <div className="grid gap-6">
          {sessions.map((session) => (
            <Card key={session.id} className="p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                <div className="flex flex-col gap-1 mb-2">
                    <div className="flex items-center gap-3">
                      <h3 className="text-xl font-semibold">
                        {session.coaching_templates?.name}
                      </h3>
                      <Badge variant={
                        session.status === 'completed' ? 'default' :
                        session.status === 'in_progress' ? 'secondary' : 'outline'
                      }>
                        {session.status}
                      </Badge>
                    </div>
                    {session.document_title && (
                      <p className="text-sm text-muted-foreground">
                        {session.document_title}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {format(new Date(session.session_start), 'MMM d, yyyy')}
                    </div>
                    {session.readiness_score && (
                      <div className="flex items-center gap-2">
                        <span>Readiness Score:</span>
                        <span className="font-semibold text-primary">
                          {session.readiness_score}%
                        </span>
                      </div>
                    )}
                  </div>

                  {session.document_title && (
                    <div className="flex items-center gap-2 text-sm">
                      <FileText className="w-4 h-4 text-muted-foreground" />
                      <span className="text-muted-foreground">
                        {session.coaching_templates?.output_document}
                      </span>
                    </div>
                  )}
                </div>

                <Button
                  variant="outline"
                  onClick={() => navigate(`/coaching/results/${session.session_id}`)}
                >
                  View Details
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
